import { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { InstallAppModal } from '@/components/InstallAppModal';

const DISMISS_KEY = 'pwa-banner-dismissed';
const DISMISS_FOREVER_KEY = 'pwa-banner-dismissed-forever';
const PAGE_VIEW_KEY = 'pwa-page-views';

function getDismissedUntil(): number {
  try { return parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10); } catch { return 0; }
}
function isDismissedForever(): boolean {
  try { const ts = parseInt(localStorage.getItem(DISMISS_FOREVER_KEY) || '0', 10); return ts > Date.now(); } catch { return false; }
}
function getPageViews(): number {
  try { return parseInt(sessionStorage.getItem(PAGE_VIEW_KEY) || '0', 10); } catch { return 0; }
}
function incrementPageViews(): number {
  const views = getPageViews() + 1;
  try { sessionStorage.setItem(PAGE_VIEW_KEY, String(views)); } catch {}
  return views;
}

export function MobileInstallBanner() {
  const { isInstalled } = usePWAInstall();
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (window.innerWidth >= 1024 || isInstalled) return;
    if (getDismissedUntil() > Date.now()) return;
    if (isDismissedForever()) return;
    const views = incrementPageViews();
    if (views >= 2) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isInstalled]);

  if (!visible) return null;

  const dismiss = (days: number) => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000)); } catch {}
    setVisible(false);
  };
  const dismissForever = () => {
    try { localStorage.setItem(DISMISS_FOREVER_KEY, String(Date.now() + 90 * 24 * 60 * 60 * 1000)); } catch {}
    setVisible(false);
  };

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-[60] bg-primary text-primary-foreground px-4 py-3 shadow-lg animate-in slide-in-from-bottom duration-300 lg:hidden">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <Download className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Prenesi aplikacijo</p>
            <p className="text-xs opacity-80 truncate">Hiter dostop z domačega zaslona</p>
          </div>
          <button
            onClick={() => { setVisible(false); setModalOpen(true); }}
            className="bg-primary-foreground text-primary text-xs font-semibold px-3 py-1.5 rounded-md flex-shrink-0"
          >
            Namesti
          </button>
          <button onClick={() => dismiss(30)} className="p-1 opacity-70 hover:opacity-100 flex-shrink-0" aria-label="Zapri">
            <X className="w-4 h-4" />
          </button>
        </div>
        <button onClick={dismissForever} className="text-[10px] opacity-50 hover:opacity-80 underline mt-1 block mx-auto">
          Ne prikazuj več
        </button>
      </div>
      <InstallAppModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}
